import { useEffect, useReducer, useRef, useState } from 'react';
import { fmt, useI18n } from '@/i18n';
import { getContainer, type SingleProductKind } from '@/configurator/product/model';
import { Stage, type StageApi } from '@/configurator/product/Stage';
import { STEP_IDS, initialState, persistable, productReducer, stepBlocker, type ProductState } from '@/configurator/product/state';
import { StepContainer, StepEditor, StepGlass, StepPrint, StepQuantity, StepUpload } from '@/configurator/product/steps';
import { StepRfq } from '@/configurator/product/StepRfq';
import { ConfiguratorHeader } from '@/configurator/ConfiguratorHeader';
import '@/styles/configurator.css';

const draftKey = (p: SingleProductKind) => `rdk.${p}.draft.v2`;

function loadDraft(p: SingleProductKind): Partial<ProductState> {
  try {
    const raw = sessionStorage.getItem(draftKey(p));
    const draft = raw ? (JSON.parse(raw) as Partial<ProductState>) : {};
    // Ignore drafts that point to a container of another product or an unknown material.
    if (draft.containerId && getContainer(draft.containerId).product !== p) return {};
    if (draft.material && draft.containerId && !getContainer(draft.containerId).materials.includes(draft.material)) return {};
    return draft;
  } catch {
    return {};
  }
}

export function ProductConfiguratorPage({ product }: { product: SingleProductKind }) {
  return <ProductConfigurator key={product} product={product} />;
}

function ProductConfigurator({ product }: { product: SingleProductKind }) {
  const { d } = useI18n();
  const f = d.cfg.families[product];
  const [s, dispatch] = useReducer(productReducer, product, (p) => ({ ...initialState(p), ...loadDraft(p), product: p }));
  const stageRef = useRef<StageApi | null>(null);
  /** Slow turntable until the visitor turns the product themselves. */
  const [turntable, setTurntable] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey(product), JSON.stringify(persistable(s)));
    } catch {
      /* storage unavailable */
    }
  }, [s, product]);

  useEffect(() => {
    document.title = `${f.title} — RDK`;
  }, [f]);

  const stepId = STEP_IDS[s.step];
  const blocker = stepBlocker(s, stepId);
  const c = getContainer(s.containerId);
  const editing = stepId === 'editor';
  const stepLabel = (id: (typeof STEP_IDS)[number]) => (id === 'glass' ? f.materialStep : d.cfg.steps[id]);

  const go = (n: number) => {
    dispatch({ type: 'goto', step: n });
    requestAnimationFrame(() => {
      const top = panelRef.current?.getBoundingClientRect().top ?? 0;
      if (top < 0 || window.innerWidth < 900) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const canReach = (i: number) => {
    if (i <= s.maxStep) return true;
    for (let k = 0; k < i; k++) if (stepBlocker(s, STEP_IDS[k])) return false;
    return i === s.maxStep + 1;
  };

  return (
    <main className="cfg" id="main">
      <ConfiguratorHeader
        current={product}
        title={f.intro}
        steps={STEP_IDS.map((id, i) => ({
          id,
          label: stepLabel(id),
          state: i === s.step ? 'current' : i <= s.maxStep ? 'done' : 'todo',
          enabled: canReach(i),
        }))}
        onStep={go}
      />

      <div className="cfg__body wrap">
        <div className="cfg__stage">
          <div className="stage-frame">
            <div className="stage-frame__meta mono">
              <span>{c.name}</span>
              <span>
                Ø {c.diameterMm} × {c.heightMm} мм
              </span>
              <span>{d.cfg.materials[s.material].label}</span>
            </div>
            <Stage
              apiRef={stageRef}
              container={c}
              material={s.material}
              ink={s.ink}
              image={s.artwork?.image ?? null}
              placement={s.placement}
              view={editing ? s.view : 'front'}
              viewAngleDeg={s.viewAngleDeg}
              onViewAngleChange={(deg) => {
                setTurntable(false);
                dispatch({ type: 'viewAngle', deg });
              }}
              autoRotate={turntable && !editing && stepId !== 'rfq' ? 14 : 0}
              editable={editing}
              onPlacementChange={(p) => dispatch({ type: 'placement', placement: p })}
              showGuides={stepId === 'container' || stepId === 'upload' || editing}
              labels={{ printBand: d.cfg.editor.printBand, seam: d.cfg.editor.seam, stageLabel: d.cfg.editor.stageLabel, noPrint: d.cfg.editor.noPrint }}
            />
            {!editing && (s.artwork || c.handle) && (
              <div className="stage-frame__spin">
                <label htmlFor="spin" className="mono">
                  {d.cfg.editor.rotateView}
                </label>
                <input id="spin" type="range" min="0" max="359" value={Math.round(s.viewAngleDeg)} onChange={(e) => {
                    setTurntable(false);
                    dispatch({ type: 'viewAngle', deg: Number(e.target.value) });
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="cfg__panel" ref={panelRef}>
          <p className="cfg__stepof mono">{fmt(d.cfg.stepOf, { n: s.step + 1, total: STEP_IDS.length })}</p>
          {stepId === 'container' && <StepContainer s={s} dispatch={dispatch} />}
          {stepId === 'glass' && <StepGlass s={s} dispatch={dispatch} />}
          {stepId === 'upload' && <StepUpload s={s} dispatch={dispatch} />}
          {stepId === 'editor' && <StepEditor s={s} dispatch={dispatch} />}
          {stepId === 'print' && <StepPrint s={s} dispatch={dispatch} />}
          {stepId === 'quantity' && <StepQuantity s={s} dispatch={dispatch} />}
          {stepId === 'rfq' && <StepRfq s={s} dispatch={dispatch} stage={stageRef} />}

          {stepId !== 'rfq' && (
            <div className="cfg__nav">
              {blocker && <p className="cfg__blocker">{d.cfg.nextRequires[blocker]}</p>}
              <button type="button" className="btn btn--ghost" disabled={s.step === 0} onClick={() => go(s.step - 1)}>
                {d.common.back}
              </button>
              <button type="button" className="btn btn--primary" disabled={!!blocker || (stepId === 'quantity' && s.quantity < 1)} onClick={() => go(s.step + 1)}>
                {d.common.next}: {stepLabel(STEP_IDS[s.step + 1])}
              </button>
            </div>
          )}
          {stepId === 'rfq' && (
            <div className="cfg__nav">
              <button type="button" className="btn btn--ghost" onClick={() => go(s.step - 1)}>
                {d.common.back}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
